import { useState, KeyboardEvent, useRef, useEffect } from "react";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { cn } from "@/app/lib/utils";
import { usePageContext } from "@/app/lib/hooks/usePageContext";

interface MessageInputProps {
  onSendMessage: (message: string, context?: any) => void;
}

export function MessageInput({ onSendMessage }: MessageInputProps) {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pageContext = usePageContext();

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message.trim(), pageContext);
      setMessage("");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to get accurate scrollHeight
      textareaRef.current.style.height = 'auto';
      
      // Calculate new height
      const lineHeight = 20; // Approximate line height in pixels
      const minHeight = lineHeight; // 1 line
      const maxHeight = lineHeight * 4; // 4 lines
      
      const newHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
      const finalHeight = Math.max(newHeight, minHeight);
      
      textareaRef.current.style.height = `${finalHeight}px`;
    }
  }, [message]);

  // Debug mode - show context (can be toggled via env var or setting)
  const showContextDebug = process.env.NODE_ENV === 'development' && false; // Set to true to debug

  return (
    <div className="border-t border-gray-200 p-3">
      {showContextDebug && pageContext && (
        <div className="mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
          <div className="font-medium mb-1">Context:</div>
          <div>Page: {pageContext.pageType}</div>
          {pageContext.entities.projectName && (
            <div>Project: {pageContext.entities.projectName}</div>
          )}
          {pageContext.entities.customerName && (
            <div>Customer: {pageContext.entities.customerName}</div>
          )}
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
          className={cn(
            "flex-1 resize-none rounded-lg border border-gray-200",
            "px-3 py-2 text-sm leading-5",
            "focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500",
            "overflow-y-auto min-h-[36px] max-h-[96px]",
            "transition-all duration-150"
          )}
          style={{ height: 'auto' }}
          rows={1}
        />
        <button
          onClick={handleSend}
          disabled={!message.trim()}
          className={cn(
            "p-2 rounded-lg transition-all duration-150",
            "disabled:opacity-40 disabled:cursor-not-allowed",
            message.trim()
              ? "bg-blue-500 hover:bg-blue-600 text-white"
              : "bg-gray-100 text-gray-400"
          )}
          aria-label="Send message"
        >
          <PaperAirplaneIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-1 text-xs text-gray-400">
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>
  );
}